import { useLocalStorage } from '@uidotdev/usehooks';
import { ReactNode, createContext, useCallback, useMemo } from 'react';
import config from '~/config';
import { CustomizationProps, I18n, ThemeDirection, ThemeMode } from '~/types/config';

const initialState: CustomizationProps = {
  ...config,
  onChangeContainer: () => {},
  onChangeLocalization: (lang: I18n) => {},
  onChangeMode: (mode: ThemeMode) => {},
  onChangeDirection: (direction: ThemeDirection) => {},
  onChangeMiniDrawer: (miniDrawer: boolean) => {},
};

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
  children: ReactNode;
  /**
   * When set, the provided mode wins over the persisted `config.mode` for the
   * visible context value. Useful for callers that want to drive the theme
   * declaratively.
   */
  mode?: ThemeMode;
};

function ConfigProvider({ children, mode: modeOverride }: ConfigProviderProps) {
  const [config, setConfig] = useLocalStorage('topstep-dashboard-config', initialState);

  const onChangeContainer = useCallback(() => {
    setConfig((prev) => ({ ...prev, container: !prev.container }));
  }, [setConfig]);

  const onChangeLocalization = useCallback(
    (lang: I18n) => {
      setConfig((prev) => ({ ...prev, i18n: lang }));
    },
    [setConfig],
  );

  const onChangeMode = useCallback(
    (mode: ThemeMode) => {
      setConfig((prev) => ({ ...prev, mode }));
    },
    [setConfig],
  );

  const onChangeDirection = useCallback(
    (direction: ThemeDirection) => {
      setConfig((prev) => ({ ...prev, themeDirection: direction }));
    },
    [setConfig],
  );

  const onChangeMiniDrawer = useCallback(
    (miniDrawer: boolean) => {
      setConfig((prev) => ({ ...prev, miniDrawer }));
    },
    [setConfig],
  );

  const value = useMemo(
    () => ({
      ...config,
      mode: modeOverride ?? config.mode,
      onChangeContainer,
      onChangeLocalization,
      onChangeMode,
      onChangeDirection,
      onChangeMiniDrawer,
    }),
    [
      config,
      modeOverride,
      onChangeContainer,
      onChangeLocalization,
      onChangeMode,
      onChangeDirection,
      onChangeMiniDrawer,
    ],
  );

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export { ConfigProvider, ConfigContext };
